import previews from './musicPresetPreviews.json';

// Pre-built prompts for the AI Music Generator (/music-generator).
//
// A preset fills the form for whichever mode is open:
// - Custom Mode: `style` (+ `exclude`) go into Style and Exclude styles; the
//   user's own lyrics are never touched.
// - Description mode: `idea` (translated) goes into Song Description.
//
// Style prompts stay in English on purpose: Suno reads genre and production
// terms best in English, and they are what lands in the Style field. Titles,
// descriptions, ideas and category names are translation keys in the
// `dashboard-music-generator` namespace.
//
// Every style is at most FREE_STYLE_MAX characters so a preset works on the
// Free plan too (scripts/test-music-presets.ts checks it). Previews (a short
// clip + cover made from the exact style) come from
// scripts/generate-music-preset-previews.ts, which writes
// musicPresetPreviews.json.

export const FREE_STYLE_MAX = 200;
// Suno V6 accepts up to 1000 characters of style and of exclusions.
export const PAID_STYLE_MAX = 1000;
export const NEGATIVE_TAGS_MAX = 1000;

export type MusicPresetCategory =
  | 'pop'
  | 'hiphop'
  | 'rock'
  | 'country'
  | 'electronic'
  | 'soul'
  | 'world'
  | 'cinematic'
  | 'occasions'
  | 'content';

export type MusicPresetVocal = 'm' | 'f' | '';

export interface MusicPreset {
  id: string;
  category: MusicPresetCategory;
  /** Translation key. */
  title: string;
  /** Translation key, one short line. */
  description: string;
  /** English style prompt for Custom Mode. */
  style: string;
  /** English exclusions (Suno's negative tags). */
  exclude?: string;
  vocalGender: MusicPresetVocal;
  instrumental: boolean;
  /** Translation key: a complete Song Description for description mode. */
  idea: string;
  /** Card colours while the cover loads (and if there is no preview). */
  gradient: [string, string];
}

export interface MusicPresetPreview {
  audio: string;
  cover: string;
  /** Seconds. */
  duration: number;
}

export const MUSIC_PRESET_CATEGORIES: Array<{
  id: MusicPresetCategory;
  label: string;
}> = [
  { id: 'pop', label: 'Pop' },
  { id: 'hiphop', label: 'Hip-Hop & R&B' },
  { id: 'rock', label: 'Rock' },
  { id: 'country', label: 'Country & Folk' },
  { id: 'electronic', label: 'Electronic' },
  { id: 'soul', label: 'Soul, Jazz & Gospel' },
  { id: 'world', label: 'Latin & World' },
  { id: 'cinematic', label: 'Cinematic & Instrumental' },
  { id: 'occasions', label: 'Occasions' },
  { id: 'content', label: 'Content & Business' },
];

export const MUSIC_PRESETS: MusicPreset[] = [
  // Pop
  {
    id: 'modern-pop-anthem',
    category: 'pop',
    title: 'Modern Pop Anthem',
    description: 'Big, radio-ready chorus and punchy drums',
    style:
      'modern pop, uplifting, anthemic, punchy drums, bright synths, catchy hook, powerful female vocals, polished radio production, 120 BPM',
    vocalGender: 'f',
    instrumental: false,
    idea: 'An uplifting modern pop anthem about chasing your dreams, with a huge catchy chorus',
    gradient: ['#f472b6', '#8b5cf6'],
  },
  {
    id: 'acoustic-pop',
    category: 'pop',
    title: 'Acoustic Pop',
    description: 'Warm guitar, soft claps, intimate vocals',
    style:
      'acoustic pop, warm, heartfelt, fingerpicked acoustic guitar, soft hand claps, light percussion, intimate male vocals, airy harmonies, organic production',
    vocalGender: 'm',
    instrumental: false,
    idea: 'A warm acoustic pop song about a summer road trip with your best friends',
    gradient: ['#fbbf24', '#f97316'],
  },
  {
    id: 'synth-pop-80s',
    category: 'pop',
    title: '80s Synth-Pop',
    description: 'Retro drum machines and shimmering synths',
    style:
      '80s synth-pop, nostalgic, dreamy, gated reverb drums, analog synth arpeggios, shimmering pads, slap bass, emotive male vocals, retro production',
    vocalGender: 'm',
    instrumental: false,
    idea: 'A nostalgic 80s synth-pop song about driving through the city at night under neon lights',
    gradient: ['#22d3ee', '#d946ef'],
  },
  {
    id: 'piano-ballad',
    category: 'pop',
    title: 'Piano Ballad',
    description: 'Emotional piano, strings and a soaring voice',
    style:
      'pop ballad, emotional, tender, grand piano, soft strings, slow build, soaring female vocals, intimate verses, big final chorus, 70 BPM',
    vocalGender: 'f',
    instrumental: false,
    idea: 'An emotional piano ballad about losing someone and finding the strength to carry on',
    gradient: ['#94a3b8', '#475569'],
  },

  // Hip-hop & R&B
  {
    id: 'boom-bap',
    category: 'hiphop',
    title: '90s Boom Bap',
    description: 'Dusty drum breaks and a confident flow',
    style:
      'boom bap hip-hop, 90s, gritty, dusty drum breaks, vinyl crackle, jazzy piano sample, deep bass, confident male rap, clear delivery, 90 BPM',
    vocalGender: 'm',
    instrumental: false,
    idea: 'A 90s boom bap hip-hop track about growing up in the city and working hard for success',
    gradient: ['#a16207', '#1c1917'],
  },
  {
    id: 'modern-trap',
    category: 'hiphop',
    title: 'Modern Trap',
    description: 'Hard 808s, rolling hi-hats, melodic hooks',
    style:
      'trap, dark, energetic, hard 808 bass, rolling hi-hats, snappy snares, eerie bell melody, melodic male rap, catchy chant hook, 140 BPM',
    vocalGender: 'm',
    instrumental: false,
    idea: 'A hard-hitting trap song about leveling up and proving everyone wrong',
    gradient: ['#7c3aed', '#0f172a'],
  },
  {
    id: 'rnb-slow-jam',
    category: 'hiphop',
    title: '90s R&B Slow Jam',
    description: 'Silky vocals, warm keys, laid-back groove',
    style:
      '90s R&B slow jam, smooth, sensual, warm Rhodes keys, deep bass groove, soft finger snaps, silky male vocals, lush harmonies, 70 BPM',
    vocalGender: 'm',
    instrumental: false,
    idea: 'A smooth 90s R&B slow jam about missing someone late at night',
    gradient: ['#be123c', '#4c1d95'],
  },

  // Rock
  {
    id: 'classic-rock',
    category: 'rock',
    title: 'Classic Rock Anthem',
    description: 'Crunchy guitars, big drums, a guitar solo',
    style:
      'classic rock, anthemic, energetic, crunchy electric guitars, driving bass, big live drums, raspy male vocals, guitar solo, stadium sound, 130 BPM',
    vocalGender: 'm',
    instrumental: false,
    idea: 'A classic rock anthem about freedom on the open road',
    gradient: ['#dc2626', '#7c2d12'],
  },
  {
    id: 'indie-rock',
    category: 'rock',
    title: 'Indie Rock',
    description: 'Jangly guitars and raw, shout-along energy',
    style:
      'indie rock, raw, energetic, jangly guitars, driving bass, tight drums, earnest male vocals, gang vocals in the chorus, garage production, 125 BPM',
    vocalGender: 'm',
    instrumental: false,
    idea: 'An energetic indie rock song about a wild night out with friends',
    gradient: ['#65a30d', '#0f766e'],
  },
  {
    id: 'pop-punk',
    category: 'rock',
    title: 'Pop Punk',
    description: 'Fast, loud and full of attitude',
    style:
      'pop punk, fast, rebellious, distorted power chords, palm-muted guitars, punchy drums, energetic male vocals, shout-along chorus, 170 BPM',
    vocalGender: 'm',
    instrumental: false,
    idea: 'A fast pop punk song about skipping class and refusing to grow up',
    gradient: ['#f43f5e', '#facc15'],
  },

  // Country & Folk
  {
    id: 'modern-country',
    category: 'country',
    title: 'Modern Country',
    description: 'Twang, a story and a sing-along chorus',
    style:
      'modern country, heartfelt, storytelling, acoustic and electric guitars, pedal steel, banjo accents, warm male vocals, sing-along chorus, Nashville production',
    vocalGender: 'm',
    instrumental: false,
    idea: 'A modern country song about a small hometown and a first love',
    gradient: ['#d97706', '#78350f'],
  },
  {
    id: 'campfire-folk',
    category: 'country',
    title: 'Campfire Folk',
    description: 'An acoustic duet that feels close and honest',
    style:
      'indie folk, intimate, honest, fingerpicked acoustic guitar, soft harmonica, gentle stomps and claps, warm male and female duet vocals, raw room recording',
    vocalGender: '',
    instrumental: false,
    idea: 'An intimate folk song about friends singing around a campfire under the stars',
    gradient: ['#ea580c', '#1e293b'],
  },

  // Electronic
  {
    id: 'deep-house',
    category: 'electronic',
    title: 'Deep House',
    description: 'A hypnotic four-on-the-floor groove',
    style:
      'deep house, groovy, hypnotic, four-on-the-floor kick, warm bassline, lush chords, chopped vocal samples, soulful female vocal hook, 122 BPM',
    vocalGender: 'f',
    instrumental: false,
    idea: 'A groovy deep house track about dancing until sunrise',
    gradient: ['#0ea5e9', '#312e81'],
  },
  {
    id: 'festival-edm',
    category: 'electronic',
    title: 'Festival EDM',
    description: 'A huge build-up and an explosive drop',
    style:
      'festival EDM, euphoric, high energy, supersaw synths, big build-up, explosive drop, punchy kick, uplifting female vocals, 128 BPM',
    vocalGender: 'f',
    instrumental: false,
    idea: 'A euphoric festival EDM song about feeling alive with thousands of people',
    gradient: ['#a855f7', '#06b6d4'],
  },
  {
    id: 'synthwave',
    category: 'electronic',
    title: 'Synthwave Drive',
    description: 'Retro-futuristic synths, no vocals',
    style:
      'synthwave, retro-futuristic, driving, analog synth bass, gated drums, soaring lead synth, neon night atmosphere, cinematic 80s, instrumental',
    exclude: 'vocals',
    vocalGender: '',
    instrumental: true,
    idea: 'A retro synthwave instrumental for a night drive through a neon city',
    gradient: ['#ec4899', '#1e1b4b'],
  },

  // Soul, Jazz & Gospel
  {
    id: 'smooth-jazz',
    category: 'soul',
    title: 'Smooth Jazz',
    description: 'Saxophone and soft keys for late nights',
    style:
      'smooth jazz, relaxed, elegant, saxophone lead, soft electric piano, brushed drums, upright bass, late-night lounge, instrumental',
    exclude: 'vocals',
    vocalGender: '',
    instrumental: true,
    idea: 'A relaxed smooth jazz instrumental for a late-night lounge',
    gradient: ['#b45309', '#1f2937'],
  },
  {
    id: 'delta-blues',
    category: 'soul',
    title: 'Delta Blues',
    description: 'Slide guitar and a gritty, soulful voice',
    style:
      'delta blues, gritty, soulful, slide guitar, stomping rhythm, harmonica, raw male vocals, call and response, vintage recording',
    vocalGender: 'm',
    instrumental: false,
    idea: 'A gritty blues song about hard times and better days ahead',
    gradient: ['#1d4ed8', '#111827'],
  },
  {
    id: 'motown-soul',
    category: 'soul',
    title: 'Motown Soul',
    description: 'Horns, tambourine and joyful harmonies',
    style:
      '60s Motown soul, joyful, groovy, tambourine, horn section, walking bass, bright piano, soulful female lead vocals, backing harmonies',
    vocalGender: 'f',
    instrumental: false,
    idea: 'A joyful 60s Motown soul song about the happiest day of your life',
    gradient: ['#f59e0b', '#be185d'],
  },
  {
    id: 'gospel-choir',
    category: 'soul',
    title: 'Gospel Choir',
    description: 'An uplifting choir, organ and hand claps',
    style:
      'gospel, uplifting, joyful, full choir, Hammond organ, piano, hand claps, powerful female lead vocals, call and response, church atmosphere',
    vocalGender: 'f',
    instrumental: false,
    idea: 'An uplifting gospel song about faith, hope and gratitude',
    gradient: ['#fde047', '#c2410c'],
  },

  // Latin & World
  {
    id: 'reggaeton',
    category: 'world',
    title: 'Reggaeton',
    description: 'Dembow rhythm and summer heat',
    style:
      'reggaeton, sensual, summer vibes, dembow rhythm, deep bass, latin percussion, catchy hook, smooth male vocals, 95 BPM',
    vocalGender: 'm',
    instrumental: false,
    idea: 'A summer reggaeton song about dancing all night on the beach',
    gradient: ['#f97316', '#db2777'],
  },
  {
    id: 'afrobeats',
    category: 'world',
    title: 'Afrobeats',
    description: 'Bouncy percussion and sunny melodies',
    style:
      'afrobeats, bouncy, sunny, log drum, syncopated percussion, smooth guitar licks, warm bass, melodic male vocals, feel-good groove, 105 BPM',
    vocalGender: 'm',
    instrumental: false,
    idea: 'A feel-good afrobeats song about a sunny day with someone special',
    gradient: ['#16a34a', '#facc15'],
  },
  {
    id: 'bossa-nova',
    category: 'world',
    title: 'Bossa Nova',
    description: 'Nylon guitar and a soft, breezy rhythm',
    style:
      'bossa nova, breezy, romantic, nylon-string guitar, soft brushed drums, upright bass, gentle flute, soft female vocals, relaxed tempo',
    vocalGender: 'f',
    instrumental: false,
    idea: 'A breezy bossa nova love song about a café by the sea',
    gradient: ['#14b8a6', '#fcd34d'],
  },

  // Cinematic & Instrumental
  {
    id: 'epic-trailer',
    category: 'cinematic',
    title: 'Epic Trailer',
    description: 'Massive drums, brass and choir',
    style:
      'epic cinematic trailer, powerful, heroic, massive taiko drums, soaring brass, choir, rising strings, dramatic build, orchestral hits, instrumental',
    vocalGender: '',
    instrumental: true,
    idea: "An epic cinematic trailer score for a hero's final battle",
    gradient: ['#b91c1c', '#0c0a09'],
  },
  {
    id: 'emotional-score',
    category: 'cinematic',
    title: 'Emotional Film Score',
    description: 'Piano and strings that tell a story',
    style:
      'emotional film score, hopeful, touching, solo piano, warm string ensemble, gentle swells, slow build, cinematic, instrumental, 70 BPM',
    exclude: 'vocals, drums',
    vocalGender: '',
    instrumental: true,
    idea: 'A touching film score with piano and strings for a heartfelt reunion scene',
    gradient: ['#60a5fa', '#1e3a8a'],
  },
  {
    id: 'lofi-study',
    category: 'cinematic',
    title: 'Lo-fi Study Beats',
    description: 'Dusty drums, warm keys, rain outside',
    style:
      'lo-fi hip-hop, chill, cozy, dusty drums, vinyl crackle, warm Rhodes chords, mellow bass, soft rain ambience, study beats, instrumental, 80 BPM',
    exclude: 'vocals',
    vocalGender: '',
    instrumental: true,
    idea: 'Cozy lo-fi study beats with rain falling outside the window',
    gradient: ['#a78bfa', '#334155'],
  },
  {
    id: 'ambient-calm',
    category: 'cinematic',
    title: 'Ambient Calm',
    description: 'Soft pads for focus and meditation',
    style:
      'ambient, calm, meditative, soft evolving pads, gentle piano notes, airy textures, slow tempo, spacious reverb, instrumental',
    exclude: 'drums, vocals',
    vocalGender: '',
    instrumental: true,
    idea: 'A calm ambient soundscape for meditation and deep focus',
    gradient: ['#5eead4', '#164e63'],
  },

  // Occasions
  {
    id: 'birthday',
    category: 'occasions',
    title: 'Birthday Song',
    description: 'Joyful, personal and made to share',
    style:
      'upbeat acoustic pop, joyful, celebratory, bright acoustic guitar, ukulele, hand claps, glockenspiel, warm group vocals, sing-along chorus',
    vocalGender: '',
    instrumental: false,
    idea: 'A joyful birthday song for my sister Emma, who loves dancing, travel and her cat Milo',
    gradient: ['#fb7185', '#fbbf24'],
  },
  {
    id: 'wedding-first-dance',
    category: 'occasions',
    title: 'Wedding First Dance',
    description: 'Romantic, tender and timeless',
    style:
      'romantic ballad, tender, timeless, soft piano, warm acoustic guitar, gentle strings, intimate male and female duet vocals, slow waltz feel',
    vocalGender: '',
    instrumental: false,
    idea: 'A romantic first-dance song for Anna and James, who met in a bookstore',
    gradient: ['#fda4af', '#a78bfa'],
  },
  {
    id: 'kids-song',
    category: 'occasions',
    title: 'Kids Sing-Along',
    description: 'Playful, simple and easy to sing',
    style:
      "children's song, playful, cheerful, ukulele, xylophone, hand claps, simple melody, bright friendly vocals, easy sing-along, 110 BPM",
    vocalGender: '',
    instrumental: false,
    idea: 'A playful kids song about brushing your teeth before bed',
    gradient: ['#4ade80', '#38bdf8'],
  },
  {
    id: 'lullaby',
    category: 'occasions',
    title: 'Lullaby',
    description: 'Soft and soothing for bedtime',
    style:
      'lullaby, soft, soothing, music box, gentle acoustic guitar, warm pads, slow tempo, gentle whispery female vocals',
    vocalGender: 'f',
    instrumental: false,
    idea: 'A gentle lullaby for baby Leo about the moon and the stars watching over him',
    gradient: ['#c4b5fd', '#1e1b4b'],
  },

  // Content & Business
  {
    id: 'jingle',
    category: 'content',
    title: 'Brand Jingle',
    description: 'Short, catchy and memorable',
    style:
      'catchy commercial jingle, upbeat, friendly, bright ukulele, whistling, hand claps, cheerful group vocals, short memorable hook',
    vocalGender: '',
    instrumental: false,
    idea: 'A catchy jingle for Sunny Bakery on Main Street, fresh bread every morning',
    gradient: ['#facc15', '#22c55e'],
  },
  {
    id: 'podcast-intro',
    category: 'content',
    title: 'Podcast Intro',
    description: 'Confident and modern, no vocals',
    style:
      'podcast intro music, confident, modern, punchy drums, funky bass, bright synth stabs, short energetic intro, instrumental',
    exclude: 'vocals',
    vocalGender: '',
    instrumental: true,
    idea: 'Energetic, modern intro music for a business podcast',
    gradient: ['#6366f1', '#0f172a'],
  },
];

/** Shown first in the form's preset strip and the gallery's Popular filter. */
export const POPULAR_MUSIC_PRESET_IDS = [
  'modern-pop-anthem',
  'rnb-slow-jam',
  'modern-country',
  'lofi-study',
  'classic-rock',
  'acoustic-pop',
  'epic-trailer',
  'birthday',
];

const PREVIEWS = previews as Record<string, MusicPresetPreview>;

export function getMusicPresetPreview(id: string): MusicPresetPreview | null {
  return PREVIEWS[id] ?? null;
}

export function getMusicPreset(id: unknown): MusicPreset | null {
  if (typeof id !== 'string') return null;
  return MUSIC_PRESETS.find((preset) => preset.id === id) ?? null;
}

export const POPULAR_MUSIC_PRESETS: MusicPreset[] =
  POPULAR_MUSIC_PRESET_IDS.map((id) => getMusicPreset(id)).filter(
    (preset): preset is MusicPreset => !!preset,
  );

// Style tags users can add to (or remove from) the Style field with one
// click. English, like the presets, because they are model input.
export const MUSIC_STYLE_TAG_GROUPS: Array<{
  id: string;
  label: string;
  tags: string[];
}> = [
  {
    id: 'genre',
    label: 'Genre',
    tags: [
      'pop',
      'rock',
      'hip-hop',
      'R&B',
      'country',
      'folk',
      'soul',
      'jazz',
      'blues',
      'gospel',
      'EDM',
      'house',
      'trap',
      'lo-fi',
      'reggae',
      'reggaeton',
      'afrobeats',
      'K-pop',
      'metal',
      'punk',
      'indie',
      'funk',
      'disco',
      'synthwave',
      'cinematic',
      'ambient',
      'classical',
      'bossa nova',
    ],
  },
  {
    id: 'mood',
    label: 'Mood',
    tags: [
      'uplifting',
      'happy',
      'romantic',
      'heartfelt',
      'melancholic',
      'sad',
      'dark',
      'energetic',
      'chill',
      'dreamy',
      'epic',
      'nostalgic',
      'aggressive',
      'peaceful',
      'playful',
      'hopeful',
      'mysterious',
      'sensual',
    ],
  },
  {
    id: 'vocals',
    label: 'Vocals',
    tags: [
      'male vocals',
      'female vocals',
      'duet vocals',
      'choir',
      'raspy vocals',
      'soulful vocals',
      'breathy vocals',
      'powerful vocals',
      'rap verses',
      'lush harmonies',
      'falsetto',
      'spoken word',
      'gang vocals',
    ],
  },
  {
    id: 'instruments',
    label: 'Instruments',
    tags: [
      'acoustic guitar',
      'electric guitar',
      'piano',
      'Rhodes keys',
      'synths',
      'strings',
      'brass section',
      'saxophone',
      '808 bass',
      'live drums',
      'fiddle',
      'banjo',
      'ukulele',
      'Hammond organ',
      'violin',
      'flute',
      'harp',
      'harmonica',
    ],
  },
  {
    id: 'tempo',
    label: 'Tempo',
    tags: [
      'slow tempo',
      'mid-tempo',
      'upbeat',
      'fast tempo',
      '70 BPM',
      '90 BPM',
      '110 BPM',
      '120 BPM',
      '140 BPM',
      'half-time',
      'four-on-the-floor',
    ],
  },
  {
    id: 'production',
    label: 'Era & sound',
    tags: [
      '60s',
      '70s',
      '80s',
      '90s',
      '2000s',
      'modern',
      'vintage',
      'live band',
      'acoustic',
      'minimal',
      'polished radio production',
      'lo-fi production',
      'analog warmth',
      'wide reverb',
      'big chorus',
      'guitar solo',
    ],
  },
];

// Suno reads bracketed section names in lyrics as structure cues.
export const LYRIC_SECTION_TAGS = [
  'Intro',
  'Verse',
  'Pre-Chorus',
  'Chorus',
  'Bridge',
  'Instrumental',
  'Outro',
] as const;

export const LYRIC_STRUCTURE_TEMPLATE = [
  '[Intro]',
  '',
  '[Verse 1]',
  '',
  '',
  '[Pre-Chorus]',
  '',
  '',
  '[Chorus]',
  '',
  '',
  '[Verse 2]',
  '',
  '',
  '[Chorus]',
  '',
  '',
  '[Bridge]',
  '',
  '',
  '[Chorus]',
  '',
  '[Outro]',
  '',
].join('\n');
